import { useState, useEffect, useCallback, use } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faPlay,
  faCopy,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useSse } from '../../sse';
import { sseManager } from '../../sse/SseManager';
import HostQuizGamePlay from './HostQuizGamePlay';
import HostGeoPlay from './HostGeoPlay';
import HostSketcherPlay from './HostSketcherPlay';
import GameOver from './GameOver';
import GeoGameOver from './GeoGameOver';
import SketcherGameOver from './SketcherGameOver';
import { getGameLabel } from '../../util/common';

import './GameLobby.css';
import MusicToggle from './MusicToggle';
import {
  fetchActivePlayers,
  fetchGameStatusStream,
} from '../../apiservices/axiosservice/gameservice';
import { startGame } from '../../apiservices/axiosservice/gameservice';
import { fetchQrCode } from '../../apiservices/axiosservice/gameservice';
import { fetchGameDetails } from '../../apiservices/axiosservice/subscriberservice';
import { useLocation } from 'react-router-dom';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import { useLeaderboard } from '../../hooks/useLeaderboard';

export interface LobbyPlayer {
  playerId: string;
  playerName: string;
  playerIcon: string;
  status: 'ready' | 'waiting';
}

const mockPlayers: LobbyPlayer[] = [
  { playerId: '1', playerName: 'FoxRunner', playerIcon: '🦊', status: 'ready' },
  {
    playerId: '2',
    playerName: 'WhaleRider',
    playerIcon: '🐋',
    status: 'ready',
  },
  { playerId: '3', playerName: 'RocketGal', playerIcon: '🚀', status: 'ready' },
];

const iconColors = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#F9A825',
  '#66BB6A',
  '#AB47BC',
  '#FF7043',
  '#26C6DA',
];

const GameLobby: React.FC = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<LobbyPlayer[]>(mockPlayers);
  const [copied, setCopied] = useState(false);
  const { state } = useLocation();
  const gameTitle = state?.gameTitle || '';
  const totalGame = state?.totalGame || 0;
  const [gameStage, setGameStage] = useState(state?.gameStatus || 'LOBBY');
  const [gameType, setGameType] = useState(state?.gameType || '');
  const leaderBoard = useLeaderboard(gameId);
  const user = getSubscriber();
  const [qrCode, setQrCode] = useState<string>('');
  const [currentGameStatus, setCurrentGameStatus] = useState<GameStatusInfo>({
    gameId: '',
    gameType: '',
    status: 'IN_PROGRESS',
    totalRound: 10,
  });
  // this update remounts the Play component with the correct gameId and gameType after game start
  // SSE: open connection for this game
  useEffect(() => {
    if (gameId) sseManager.connect(gameId);
    return () => {
      sseManager.close();
    };
  }, [gameId]);

  const geoSync = useSse('geo-sync');
  const quizSync = useSse('quiz-sync');

  useEffect(() => {}, [geoSync, quizSync]);

  // Fetch game status on mount (handles page reload when route state is lost)
  useEffect(() => {
    if (!state && gameId) {
      fetchGameDetails(gameId)
        .then((res) => {
          if (res?.data?.gameStatus) {
            console.log('Fetched game details:', res.data);
            setGameStage(res.data.gameStatus);
            setGameType(res.data.gameType);
          }
        })
        .catch(() => {});
    }
  }, [gameId, state]);
  //SSE for Game Status info

  // SSE: listen for real-time player joins
  const playerEvent = useSse<{
    playerId: string;
    playerName: string;
    playerIcon: string;
  }>('player-joined');

  const gameStatusEvent = useSse<GameStatusInfo>('game-status');
  console.log('GameLobby - Received gameStatusEvent:', gameStatusEvent);

  useEffect(() => {
    if (gameStatusEvent) {
      setGameStage(gameStatusEvent.status);
      setCurrentGameStatus(gameStatusEvent);
    }
  }, [gameStatusEvent]);

  //Rest fetch game status
  // Game status
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        console.log('fetchGameStatusStream: ', res.data);
        setCurrentGameStatus(res.data);
        setGameStage(res.data.status);
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, []);
  // Fetch

  useEffect(() => {
    if (playerEvent) {
      setPlayers((prev) => {
        if (prev.some((p) => p.playerId === playerEvent.playerId)) return prev;
        return [...prev, { ...playerEvent, status: 'ready' }];
      });
    }
  }, [playerEvent]);

  useEffect(() => {
    // Cleanup on unmount
    const fetchPlayers = async () => {
      try {
        const response = await fetchActivePlayers(gameId!);
        setPlayers(response.data);
      } catch (error) {
        console.error('Error fetching active players:', error);
      }
    };
    fetchPlayers();
    return () => {
      setPlayers([]);
    };
  }, []);

  // Fetch QR Code by API
  useEffect(() => {
    if (gameId) {
      fetchQrCode(gameId)
        .then((res) => setQrCode(res.data.qrCodeBase64Image))
        .catch((error) => console.log('Error fetching QR code:', error));
    }
  }, [gameId]);

  const handleCopy = () => {
    const baseUrl =
      process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const url = `${baseUrl}/join?gameId=${gameId}`;
    navigator.clipboard.writeText(url!);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hostGameComponents: Record<
    string,
    React.FC<{ onClose: () => void; gameId: string | undefined }>
  > = {
    geoplay: HostGeoPlay,
    quiz: HostQuizGamePlay,
    sketcher: HostSketcherPlay,
  };
  const hostGameOvers: Record<string, React.FC<any>> = {
    geoplay: GeoGameOver,
    quiz: GameOver,
    sketcher: SketcherGameOver,
  };

  const initiateGame = useCallback(async () => {
    const request: PreviewRemoteRequest = {
      gameId,
      gameOwnerId: user?.userId,
    };

    try {
      const response = await startGame(request);
      console.log('Game started successfully2', response.data);
      console.log('Start game response:', response);
      if (response.data) {
        setGameStage(response.data.status);
      }
    } catch (err) {
      console.log('Error starting game:', err);
    }
  }, [gameId, gameType]);

  const handleClose = () => navigate(`/dashboard/games/game/${gameId}`);

  if (gameStage === 'FINISHED') {
    const GameTypeGameOver = hostGameOvers[gameType];

    return (
      <GameTypeGameOver
        onPlayAgain={handleClose}
        leaderBoard={leaderBoard}
        gameInfo={currentGameStatus}
      />
    );
  }
  if (gameStage === 'IN_PROGRESS') {
    const GameComponent = hostGameComponents[gameType];
    if (GameComponent) {
      return (
        <GameComponent
          onClose={() => {}}
          gameId={gameId}
          // key={roundReloadKey}
        />
      );
    }
  }

  return (
    <div className="lobby">
      <button className="lobby__close" onClick={handleClose}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <MusicToggle />

      <div className="lobby__content">
        {/* Left panel */}
        <div className="lobby__left">
          <h2 className="lobby__join-text">Join at funhives.com</h2>

          <div className="lobby__code-card">
            <div className="lobby__code-header">
              <span className="lobby__code-label">Game Code</span>
              <button className="lobby__copy-btn" onClick={handleCopy}>
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
            <div className="lobby__code" onClick={handleCopy}>
              {gameId}
            </div>
            <p className="lobby__code-sub">
              {copied ? 'Copied!' : 'Share this code with players to join'}
            </p>
          </div>

          {qrCode && (
            <div className="lobby__qr-card">
              <img
                className="lobby__qr-image"
                src={qrCode}
                alt="Scan to join game"
              />
            </div>
          )}

          <h3 className="lobby__game-title">{gameTitle}</h3>
          <p className="lobby__game-meta">
            {getGameLabel(gameType)} · {currentGameStatus.totalRound} Rounds
          </p>

          <button className="lobby__start-btn" onClick={initiateGame}>
            <FontAwesomeIcon icon={faPlay} />
            Start Game — {players.length} Players Ready
          </button>
        </div>

        {/* Right panel */}
        <div className="lobby__right">
          <div className="lobby__right-header">
            <h3 className="lobby__right-title">Players in Lobby</h3>
            <span className="lobby__player-count">
              <FontAwesomeIcon icon={faUsers} /> {players.length} Players Joined
            </span>
          </div>

          <div className="lobby__player-grid">
            {players.map((p, i) => (
              <div key={p.playerId} className="lobby__player-card">
                <span
                  className="lobby__player-avatar"
                  style={{ background: iconColors[i % iconColors.length] }}
                >
                  {p.playerIcon}
                </span>
                <div className="lobby__player-info">
                  <span className="lobby__player-name">{p.playerName}</span>
                  <span className="lobby__player-status">Ready</span>
                </div>
              </div>
            ))}
            {/* Waiting slot */}
            <div className="lobby__player-card lobby__player-card--waiting">
              <span className="lobby__player-avatar lobby__player-avatar--waiting">
                +
              </span>
              <span className="lobby__player-waiting-text">Waiting...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameLobby;
